import type {
  ProductInput,
  SelectedAccessoryInput,
  ProductOutput,
  AccessoryGroupId,
  SelectedAccessory,
} from "../product.types";

type SelectAccessoryArgs = SelectedAccessory & {
  input: ProductInput;
  output: ProductOutput;
};

export const selectAccessory = ({
  currentPresentation: presentation,
  accessoryId,
  accessoryGroupId,
  input,
  output,
}: SelectAccessoryArgs) => {
  if (!presentation) {
    throw new Error("Current presentation is required to select an accessory");
  }
  if (!accessoryId) {
    throw new Error("Accessory ID is required to select an accessory");
  }
  if (!accessoryGroupId) {
    throw new Error("Accessory Group ID is required to select an accessory");
  }
  if (!input?.state) {
    throw new Error("Input state is missing");
  }
  const accessoryFromGroup = getAccessoryFromGroup(
    presentation,
    accessoryGroupId,
    output,
  );
  if (!accessoryFromGroup) {
    throw new Error(
      `Accessory with presentation ${presentation} not found in group ${accessoryGroupId}`,
    );
  }
  const {
    currentPresentation,
    infoText,
    optionStates,
    pimId,
    seriesId,
    seriesName,
  } = accessoryFromGroup;

  const accessoryInput: SelectedAccessoryInput = {
    accessoryId,
    accessoryGroupId,
    currentPresentation,
    infoText,
    optionStates,
    pimId,
    seriesId,
    seriesName,
  };
  const selectedAccessories =
    input.state?.selectedAccessories?.filter(
      (a) => a?.accessoryGroupId !== accessoryInput.accessoryGroupId,
    ) || [];
  if (accessoryInput.seriesId !== 0) {
    selectedAccessories.push(accessoryInput);
  }
  input.state = {
    ...input.state,
    selectedAccessories,
  };
};

const getAccessoryFromGroup = (
  presentation: string,
  accessoryGroupId: AccessoryGroupId,
  output: ProductOutput,
) => {
  const accessoryGroups = output.product?.accessoryGroups;
  if (!accessoryGroups) {
    return null;
  }
  for (const group of accessoryGroups) {
    if (group?.id === accessoryGroupId) {
      const foundAccessory = group.accessories?.find(
        (acc) => acc?.currentPresentation === presentation,
      );
      if (foundAccessory) {
        return foundAccessory;
      }
    }
  }
  return null;
};
